import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {
  AnalyticsSuccessBannerState,
  AnalyticsSuccessBannerTypesEnum,
} from '../../../../store/panels/app/banners/bannersState';

/*
 * Props.
 */

interface AnalyticsSuccessBannerProps {
  banner: AnalyticsSuccessBannerState;
  onClose: () => void;
}

/*
 * Styles.
 */

const StyledWrappedTextDiv = styled.div`
  white-space: normal;
`;

/*
 * Component.
 */

export const AnalyticsSuccessBanner: FC<AnalyticsSuccessBannerProps> = (props) => {
  const {banner, onClose} = props;

  const theme = useTheme();

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      priority={BannerPrioritiesEnum.TRANSIENT}
      color={theme.palette.green.shade40}
    >
      <BannerIcon iconName="checkmarkCircleSmall" />
      {renderAnalyticsSuccessBannerContent(banner)}
      <BannerCloseAction onClick={onClose} />
    </Banner>
  );
};

export function renderAnalyticsSuccessBannerContent({
  analyticsSuccessType,
  payload,
}: AnalyticsSuccessBannerState) {
  switch (analyticsSuccessType) {
    case AnalyticsSuccessBannerTypesEnum.CONVERSATION_STAGES_EDITED:
      return (
        <BannerContent>
          <BannerContentTitle>
            <StyledWrappedTextDiv>
              <Trans id="banner-analytics-conversation-stages-success-title">
                Stages{' '}
                {!payload.nbCurrentInboxes ? (
                  <Trans id="banner-analytics-conversation-stages-disabled-all-inboxes">
                    disabled for all inboxes
                  </Trans>
                ) : null}
                {payload.nbInboxesEnabled > 0 ? (
                  <Plural
                    id="banner-analytics-conversation-stages-success-title-enabled-inboxes"
                    value={payload.nbInboxesEnabled}
                    one="enabled for 1 inbox"
                    other="enabled for # inboxes"
                  />
                ) : null}{' '}
                {payload.nbInboxesEnabled > 0 && payload.nbInboxesDisabled > 0 ? 'and' : null}{' '}
                {payload.nbInboxesDisabled > 0 && payload.nbCurrentInboxes ? (
                  <Plural
                    id="banner-analytics-conversation-stages-success-title-disabled-inboxes"
                    value={payload.nbInboxesDisabled}
                    one="disabled for 1 inbox"
                    other="disabled for # inboxes"
                  />
                ) : null}{' '}
                {payload.selectedTeamName ? `in ${payload.selectedTeamName}` : null}
              </Trans>
            </StyledWrappedTextDiv>
          </BannerContentTitle>
          <BannerText>
            <StyledWrappedTextDiv>
              <Trans id="banner-analytics-conversation-stages-success-description">
                Wait around 1 hour for data to update in analytics.
              </Trans>
            </StyledWrappedTextDiv>
          </BannerText>
        </BannerContent>
      );
    default:
      return null;
  }
}
